import { useMsal } from "@azure/msal-react";
import {
    getTheme, ICommandBarItemProps,
    IContextualMenuItem,
    Stack,
    Text
} from "@fluentui/react";
import { useBoolean } from '@fluentui/react-hooks';
import { AddIcon } from '@fluentui/react-icons-mdl2';
import { RootState } from "app/store";
import AccountSummary from "components/AccountSummary";
import CustomPanel from "components/controls/CustomPanel";
import SimpleCard from "components/controls/SimpleCard";
import useHiddenWithMount from "components/useHiddenWithMount";
import saveAs from "file-saver";
import { AppLinkTo, AppNavigateToExternal, compareUnderWritingReviewToWithoutDispatch, emitCustomEvent, EViewType } from "global/Application";
import { Constants } from "global/Application/Constants";
import { useApplication } from "global/Application/Context";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdateGroupConfiguration, addUpdateStopLoss, IAddFeesPayload } from "global/Reducer/dataSlice";
import { setProcessing } from "global/Reducer/statusSlice";
import { BuildVisibilityScope } from "global/Visibility/GlobalVisibility";
import { bcbs_groupconfigurationsnapshot } from "model/AttributeOptionSets.type";
import * as FAM from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RouterParams } from "../../App";
import SnapshotSummary from "../../components/SnapshotSummary";
import AccountsReceivableCard from "./AccountsReceivableCard";
import AccountsReceivableWizard from './AccountsReceivableWizard';
import AdobeCampaignExtract from "./AdobeCampaignExtract";
import BenefitDetailExtract from "./BenefitDetailExtract";
import BenefitSummaryReportWizard from "./BenefitSummaryReportWizard";
import BenefitWizard from "./BenefitWizard";
import BlueRelationshipContext from "./BlueRelationshipContext";
import ClientBenefitDetailReportWizard from "./ClientBenefitDetailReportWizard";
import CompareExtract from "./CompareExtract";
import ActionItemsPanel from "./Controls/ActionItemsPanel";
import BenefitPanel from "./Controls/BenefitPanel";
import DataValidationPanel from "./Controls/DataValidationPanel";
import PlanPanel from "./Controls/PlanPanel";
import ProcessPanel from "./Controls/ProcessPanel";
import SnapshotDiscovery from "./Controls/SnapshotDiscovery";
import EnrollmentDetailDialog from "./EnrollmentDetailDialog";
import FeesAndPremiumCard from "./FeesAndPremiumCard";
import FeesAndPremiumWizard from "./FeesAndPremiumWizard";
import FeesGrid from './FeesGrid';
import FlexibleFieldView, { EScopeType } from './FlexibleFieldView';
import GroupConfigurationProperties from "./GroupConfigurationProperties";
import GroupStructureCESView from "./GroupStructure/GroupStructureCESView/GroupStructureCESView";
import GroupStructureDepartmentView from './GroupStructureDepartmentView';
import GroupStructureGroupNumberView from "./GroupStructureGroupNumberView";
import GroupStructureWizard, { EGroupStructureWizardVariant } from "./GroupStructureWizard";
import MonthlyRecurringFeesAgg from "./MonthlyRecurringFeesAgg";
import NonStandardTemplateWizard from "./NonStandardTemplateWizard";
import PlanComparisonReport from "./PlanComparisonReport";
import PlanWizard from "./PlanWizard";
import StopLossCard from "./StopLossCard";
import StopLossWizard from "./StopLossWizard";

interface IProps extends TComponentConnectedProps {
}


const GroupConfiguration = (props: IProps) => {
    const disabled = props.state.status.viewType !== EViewType.Modify;
    const { isAuthenticated, authorizations } = useAuthentication();



    const theme = getTheme();
    const dispatch = useDispatch();

    const state = useSelector((state: RootState) => state);


    const application = useApplication();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);
    const params = useParams<RouterParams>();

    const [isBSRHidden, setBSRHidden, isBSRMounted] = useHiddenWithMount(true);

    const [isPCRHidden, setPCRHidden, isPCRMounted] = useHiddenWithMount(true);

    const [groupConfigurationPropertiesMount, setGroupConfigurationPropertiesMount] = useState(false);
    const [groupConfigurationPropertiesIsOpen, setGroupConfigurationPropertiesIsOpen] = useState(false);

    const workspace = props.state.status.workspace;


    useEffect(() => {
        // on mount, tell menu what to display
        document.title = `Group Configuration | ${props.state.data.snapshot?.bcbs_name ?? '-'}`;

    }, [props?.state?.data?.snapshot])


    useEffect(() => {
        //TODO windows animation frame/or postLayoutEffect hook if there is one => useCallback on windowAnimation frame I think
        if (history.location.search === '?navigation=pending')
            setTimeout(() => {
                let docElement: HTMLElement | null = null;

                if (params.groupNumberId) {
                    docElement = document.getElementById('groupstructure.groupnumber');
                }

                docElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

            }, 500);
    }, []);


    const menu = useMemo(() => {

        const menu: ICommandBarItemProps[] = []

        // Group Configuration Properties
        if (!disabled)
            menu.push({
                key: 'Properties',
                text: 'Properties',
                iconProps: { iconName: 'Settings' },
                onClick: () => {
                    setGroupConfigurationPropertiesIsOpen(true);
                    setGroupConfigurationPropertiesMount(true);
                }
            });

        menu.push({
            key: 'Compare',
            text: 'Compare',
            iconProps: { iconName: 'Compare' },
            onClick: () => {
                AppNavigateToExternal(history, props.state, 'compareApp');
            }
        });

        function buildReportMenu() {
            const submenu: IContextualMenuItem[] = [];

            // CBDR 
            submenu.push({
                key: 'Client Benefit Detail Report',
                text: 'Client Benefit Detail Report',
                title: 'Generate Benefit Detail Report',
                iconProps: { iconName: 'Generate' },
                onClick: () => setCBDRWizardIsOpen(true)
            });

            // BDR 
            submenu.push({
                key: 'Benefit Detail Report',
                text: 'Benefit Detail Report',
                title: 'Generate Benefit Detail Report',
                iconProps: { iconName: 'Generate' },
                onClick: () => setBDRWizardIsOpen(true)
            });

            // NSE 
            submenu.push({
                key: 'Non-Standard Extract',
                text: 'Non-Standard Extract',
                title: 'Generate Non-Standard Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    (async () => {
                        try {
                            dispatch(setProcessing(true));
                            const excelExtractImport = await import("global/ExcelExtract")
                            const { NonstandardExcelGeneration } = excelExtractImport;
                            const result = await NonstandardExcelGeneration(props.state, dataSource)
                            const fileName = `NSE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                            saveAs(result.file, fileName);
                        } catch (error) {
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });
                        } finally {
                            dispatch(setProcessing(false));
                        }
                    })();
                }
            });

            // FPE
            submenu.push({
                key: 'Fees & Premiums Extract',
                text: 'Fees & Premiums Extract',
                title: 'Generate Fees & Premiums Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    (async () => {
                        try {
                            dispatch(setProcessing(true));
                            const excelExtract = await import("global/ExcelExtract")
                            const { FeesAndPremiumsExcelGeneration } = excelExtract;
                            const result = await FeesAndPremiumsExcelGeneration(props.state, dataSource);
                            const fileName = `FPE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                            saveAs(result.file, fileName);
                        } catch (error) {
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });
                        } finally {
                            dispatch(setProcessing(false));
                        }
                    })();
                }
            });

            // CE
            submenu.push({
                key: 'Compare Extract',
                text: 'Compare Extract',
                title: 'Generate Compare Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    setCompareExtractMount(true);
                    setCompareExtractIsOpen(true);
                }
            });


            // ACE
            if (authorizations?.roles['BCBS - Power User'])
                submenu.push({
                    key: 'Adobe Campaign Extract',
                    text: 'Adobe Campaign Extract',
                    title: 'Generate Adobe Campaign Extract',
                    iconProps: { iconName: 'Generate' },
                    onClick: () => {
                        setAdobeCampaignExtractIsOpen(true);
                        setAdobeCampaignExtractMount(true);
                    }
                });

            // BDE
            if (authorizations?.roles['BCBS - Power User'])
                submenu.push({
                    key: 'Benefit Detail Extract',
                    text: 'Benefit Detail Extract',
                    title: 'Generate Benefit Detail Extract',
                    iconProps: { iconName: 'Generate' },
                    onClick: () => {
                        setBenefitDetailExtractIsOpen(true);
                        setBenefitDetailExtractMount(true);
                    }
                });

            // Pharmacy/ORE Extract
            submenu.push({
                key: 'Pharmacy/ORE Extract',
                text: 'Pharmacy/ORE Extract',
                title: 'Pharmacy/ORE Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    (async () => {
                        try {
                            dispatch(setProcessing(true));
                            const response = await dataSource.getOptumBulkExtractReports(props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid!);
                            saveAs(response.blob, response.fileName, { autoBom: true });
                        } catch (err: any) {
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: err
                            });
                            console.error(err);
                        } finally {
                            dispatch(setProcessing(false));
                        }
                    })();
                }
            });


            // NSE 
            submenu.push({
                key: 'Financial Extract',
                text: 'Financial Extract',
                title: 'Financial Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    (async () => {
                        try {
                            dispatch(setProcessing(true));
                            const excelExtractImport = await import("global/ExcelExtract")
                            const { PFABExtract } = excelExtractImport;
                            const result = await PFABExtract(props.state, dataSource)
                            const fileName = `Financial-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                            saveAs(result.file, fileName);
                        } catch (error) {
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });
                        } finally {
                            dispatch(setProcessing(false));
                        }
                    })();
                }
            });

            //Schedule A
            submenu.push({
                key: 'Schedule A',
                text: 'Schedule A',
                title: 'Generate Schedule A',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    (async () => {
                        try {
                            const ScheduleAActionImport = await import('global/Application/ScheduleAGenerateAction');
                            const { ScheduleAGenerateAction } = ScheduleAActionImport;
                            dispatch(ScheduleAGenerateAction(dataSource));
                        } catch (error) {
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });
                        }
                    })();
                }
            });

            if (Constants.env !== 'Production' || Constants.app.isLocalHost)
                submenu.push({
                    key: 'Benefit Summary Report',
                    text: 'Benefit Summary Report',
                    title: 'Benefit Summary Report',
                    iconProps: { iconName: 'Generate' },
                    onClick: () => {
                        setBSRHidden(false);
                    }
                });

            // submenu.push({
            //     key: 'Plan Comparison Report',
            //     text: 'Plan Comparison Report',
            //     title: 'Plan Comparison Report',
            //     iconProps: { iconName: 'Generate' },
            //     onClick: () => {
            //         setPCRHidden(false);
            //     }
            // });

            if (submenu.length > 0)
                return ({
                    key: 'reportsAndExtracts',
                    text: 'Reports/Extracts',
                    iconProps: { iconName: 'Generate' },
                    subMenuProps: {
                        items: submenu
                    }
                } as ICommandBarItemProps);

            return null;
        };

        const reportMenu = buildReportMenu();

        if (reportMenu)
            menu.push(reportMenu);

        // Enrollment Details
        if (disabled)
            menu.push({
                key: 'groupConfigurationEnrollmentDetail',
                text: 'Enrollment Details',
                iconProps: { iconName: 'OpenEnrollment' },
                onClick: () => {
                    setEnrollmentDetailIsOpen(true);
                    setEnrollmentDetailMount(true);
                }
            });

        // Non-Standard
        if (Constants.env !== 'Production' || Constants.app.isLocalHost) {
            menu.push({
                key: 'nonStandard',
                text: 'Non-Standards',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    history.push(AppLinkTo(props.state, { id: "nonStandardScreen", type: 'nonstandardtemplates' }))
                }
            });
        }

        menu.push({
            key: 'pharmacy',
            text: 'Pharmacy/ORE',
            iconProps: { iconName: 'Pill' },
            onClick: () => {
                const foundSnapshot = compareUnderWritingReviewToWithoutDispatch(props.state, props.state.data.snapshot);
                // TODO move this to AppNavigateToExternal
                (window as Window).location = (`/pharmacyLanding/${state.data.snapshot._bcbs_groupid_value}/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}/${foundSnapshot?.bcbs_groupconfigurationsnapshotid ?? ""}`);
            }
        });

        if (
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value ||
            props.state.status.debug.admin
        )
            menu.push({
                key: 'enmChecklist',
                text: 'E&M Checklist',
                iconProps: { iconName: 'CheckList' },
                onClick: () => {
                    history.push(AppLinkTo(props.state, { id: '', type: 'checkList' }))
                }
            });


        menu.push({
            key: 'subgroup',
            text: 'Subgroup Information',
            iconProps: { iconName: 'ViewListGroup' },
            onClick: () => { history.push(`/subgroupInformation/${state.data.snapshot._bcbs_groupid_value}/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}`) }
        });


        return { items: menu, key: 'groupConfiguration' };
    }, [props.state.status]);

    const account = props.state.status?.account;
    const plans: FAM.IPlan[] = props.state?.data.json?.plans ?? [];
    const benefits: FAM.IBenefit[] = props.state.data.json?.benefits ?? [];

    const planStopLoss: FAM.IStopLoss[] = props.state.data.json.stopLoss ?? [];
    const claimsAR: FAM.IAccountsReceivable[] = props.state.data.json.claimsARs ?? [];

    const [benefitWizardIsOpen, { setTrue: benefitWizardOpen, setFalse: benefitWizardClose }] = useBoolean(false);
    const [planWizardIsOpen, { setTrue: planWizardOpen, setFalse: planWizardClose }] = useBoolean(false);
    const [nonStandardTemplateWizardIsOpen, { setTrue: nonStandardTemplateWizardOpen, setFalse: nonStandardTemplateWizardClose }] = useBoolean(false);
    const [feesAndPremiumWizardIsOpen, { setTrue: feesAndPremiumWizardOpen, setFalse: feesAndPremiumWizardClose }] = useBoolean(false);

    const [groupStructureWizardSpecial, setGroupStructureWizardSpecial] = useState<EGroupStructureWizardVariant | null>(null);

    const [stopLossWizardIsOpen, { setTrue: stopLossWizardOpen, setFalse: stopLossWizardClose }] = useBoolean(false);

    const [ARWizardMount, setARWizardMount] = useState(false);
    const [ARWizardIsOpen, setARWizardIsOpen] = useState(false);

    const [CBDRWizardIsOpen, setCBDRWizardIsOpen] = useState(false);

    const [BDRWizardIsOpen, setBDRWizardIsOpen] = useState(false);

    const [enrollmentDetailMount, setEnrollmentDetailMount] = useState(false);
    const [enrollmentDetailIsOpen, setEnrollmentDetailIsOpen] = useState(false);

    const [nonStandardTemplateMount, setNonStandardTemplateMount] = useState(false);
    const [nonStandardTemplateIsOpen, setNonStandardTemplateIsOpen] = useState(false);

    const [compareExtractMount, setCompareExtractMount] = useState(false);
    const [compareExtractIsOpen, setCompareExtractIsOpen] = useState(false);

    const [adobeCampaignExtractMount, setAdobeCampaignExtractMount] = useState(false);
    const [adobeCampaignExtractIsOpen, setAdobeCampaignExtractIsOpen] = useState(false);


    const [benefitDetailExtractMount, setBenefitDetailExtractMount] = useState(false);
    const [benefitDetailExtractIsOpen, setBenefitDetailExtractIsOpen] = useState(false);


    const history = useHistory();

    const categoryGroupConfiguration = props.state.data.config?.cache?.map_bcbs_config_category_named["Group Configuration"];

    const groupStructureMenu: IContextualMenuItem[] = [];

    if (!disabled)
        groupStructureMenu.push(
            {
                key: 'groupConfiguration.groupStructure.manage',
                name: 'Manage',
                icon: 'DocumentManagement',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    setGroupStructureWizardSpecial(EGroupStructureWizardVariant.wizard);
                },
            },

        );

    if (disabled)
        groupStructureMenu.push(
            {
                key: 'groupConfiguration.groupStructure.export',
                name: 'Export',
                icon: 'DocumentManagement',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    setGroupStructureWizardSpecial(EGroupStructureWizardVariant.export);
                },
            },

        );

    const controlVisibility = BuildVisibilityScope(
        null,
        props.state.data.json,
        null,
        null
    );


    function onRenderPanels() {
        return <>
            {groupConfigurationPropertiesMount &&
                <GroupConfigurationProperties
                    isOpen={groupConfigurationPropertiesIsOpen}
                    onDismiss={() => setGroupConfigurationPropertiesIsOpen(false)}
                    onConfirm={(e) => {
                        setGroupConfigurationPropertiesIsOpen(false);

                        if (e?.value)
                            dispatch(addUpdateGroupConfiguration(e.value));

                    }}
                    defaultValue={props.state.data.json}
                    variant={"change"}
                />
            }

            <PlanWizard
                isOpen={planWizardIsOpen || !!groupStructureWizardSpecial}
                onDismiss={planWizardClose}
                onCancel={planWizardClose}
                variant="edit"
            />
            <BenefitWizard
                isOpen={benefitWizardIsOpen}
                onCancel={benefitWizardClose}
                onDismiss={benefitWizardClose}
            />
            <FeesAndPremiumWizard
                isOpen={feesAndPremiumWizardIsOpen}
                onCancel={feesAndPremiumWizardClose}
                onDismiss={feesAndPremiumWizardClose}
                variant={'add'}
            />
            <GroupStructureWizard
                isOpen={!!groupStructureWizardSpecial}
                onCancel={() => setGroupStructureWizardSpecial(null)}
                onDismiss={() => setGroupStructureWizardSpecial(null)}
                variant={groupStructureWizardSpecial}
            />
            <ClientBenefitDetailReportWizard
                isOpen={CBDRWizardIsOpen}
                variant={'CBDR'}
                onCancel={() => setCBDRWizardIsOpen(false)}
                onDismiss={() => setCBDRWizardIsOpen(false)}
            />
            <ClientBenefitDetailReportWizard
                isOpen={BDRWizardIsOpen}
                variant={'BDR'}
                onCancel={() => setBDRWizardIsOpen(false)}
                onDismiss={() => setBDRWizardIsOpen(false)}
            />
            <StopLossWizard
                isOpen={stopLossWizardIsOpen}
                onDismiss={stopLossWizardClose}
                onCancel={stopLossWizardClose}
                variant={"add"}
                onConfirm={(event) => {
                    const t: IAddFeesPayload[] = [
                        {
                            stopLoss: event?.value.stopLossSelected!,
                        }
                    ];

                    dispatch(addUpdateStopLoss(t));
                    stopLossWizardClose();
                }}
            />

            {ARWizardMount &&
                <AccountsReceivableWizard
                    isOpen={ARWizardIsOpen}
                    onDismiss={() => setARWizardIsOpen(false)}
                    onCancel={() => setARWizardIsOpen(false)}
                    variant="edit"
                />
            }

            {enrollmentDetailMount &&
                <EnrollmentDetailDialog
                    isOpen={enrollmentDetailIsOpen}
                    onDismiss={() => { setEnrollmentDetailIsOpen(false); }}
                    onConfirm={() => { setEnrollmentDetailIsOpen(false); }}
                />
            }

            {compareExtractMount &&
                <CompareExtract
                    isOpen={compareExtractIsOpen}
                    onDismiss={() => { setCompareExtractIsOpen(false) }}
                />
            }

            {nonStandardTemplateMount &&
                <NonStandardTemplateWizard
                    isOpen={nonStandardTemplateIsOpen}
                    onCancel={() => setNonStandardTemplateIsOpen(false)}
                    onDismiss={() => { setNonStandardTemplateIsOpen(false) }}
                />
            }

            {adobeCampaignExtractMount &&
                <AdobeCampaignExtract
                    isOpen={adobeCampaignExtractIsOpen}
                    onDismiss={() => { setAdobeCampaignExtractIsOpen(false) }}
                />
            }

            {benefitDetailExtractMount &&
                <BenefitDetailExtract
                    isOpen={benefitDetailExtractIsOpen}
                    onDismiss={() => { setBenefitDetailExtractIsOpen(false) }}
                />
            }

            {isBSRMounted &&
                <BenefitSummaryReportWizard
                    isOpen={!isBSRHidden}
                    onDismiss={() => {
                        setBSRHidden(true);
                    }}
                    onCancel={() => {
                        setBSRHidden(true);
                    }}
                />
            }


            {isPCRMounted &&
                <PlanComparisonReport
                    isOpen={!isPCRHidden}
                    onDismiss={() => {
                        setPCRHidden(true);
                    }}
                    onCancel={() => {
                        setPCRHidden(true);
                    }}
                />
            }

        </>;
    }

    function onRenderSummary() {
        return <CustomPanel title={"Summary"}
            storageId={"groupConfiguration.summary"}
            canCollapse={true}
            defaultCollapsed={true}
            mountWhenExpanded={true}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignContent: 'stretch' }}>
                {account &&
                    <AccountSummary data={props.state.data} />
                }

                {props?.state?.data?.snapshot &&
                    <SnapshotSummary data={props.state.data} />
                }
            </div>
        </CustomPanel>;
    }

    function onRenderAttributes() {
        return <CustomPanel title={"Attributes"}
            canCollapse={true}
            defaultCollapsed={params.tabId ? false : true}
            storageId={"groupConfiguration.attributes"}
            mountWhenExpanded={true}
        >
            <FlexibleFieldView
                disabled={disabled}
                fields={props.state.data.json.fields}
                tiers={[]}
                category={categoryGroupConfiguration}
                groupConfiguration={props.state.data.json}
                visibility={controlVisibility}
                cancellation={
                    props?.state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus["Cancelled"] ||
                    props?.state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus["Cancellation in Progress"] ||
                    props?.state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus["Transferred to Other LOB"] ||
                    props?.state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus["Transferred to Other LOB in Progress"]
                }
                scopeType={EScopeType.groupConfiguration}
            />
        </CustomPanel>;
    }

    function onRenderPlan() {
        return <PlanPanel
            onAdd={planWizardOpen}
        />;
    }

    function onRenderBenefit() {
        return <BenefitPanel
            onAdd={benefitWizardOpen}
        />;
    }

    function onRenderGroupStructure() {
        return <CustomPanel
            title={"Group Structure"}
            id={"groupStructure"}
            canCollapse={true}
            defaultCollapsed={params.groupNumberId ? false : true}
            storageId={"groupConfiguration.groupStructure"}
            items={groupStructureMenu}
            mountWhenExpanded={true}
        >
            <div style={{ padding: '8px' }}></div>
            <GroupStructureCESView disabled={disabled} />
            <GroupStructureGroupNumberView disabled={disabled} />
            <GroupStructureDepartmentView disabled={disabled} />
        </CustomPanel>;
    }

    function onRenderFeesAndPremium() {
        return <CustomPanel title={"Fees & Premiums"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"groupConfiguration.feesAndPremiums"}
            mountWhenExpanded={true}
        >
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '15px' }}
            >
                {props.state?.data.json?.feesAndPremiums?.map(eachFeesAndPremium => {

                    // skip deactivated records/only show deactivated records if permitted at group config properties level
                    if (eachFeesAndPremium.deactivated !== props.state.data.json.showInActives?.value)
                        return <></>;

                    return (
                        <FeesAndPremiumCard
                            key={eachFeesAndPremium.id}
                            contextPlans={props.state.data.json.plans}
                            feesAndPremium={eachFeesAndPremium}
                            state={props.state}
                            onClick={() => history.push(AppLinkTo(props.state, { id: eachFeesAndPremium.id, type: 'feesAndPremium' }))}
                        />
                    );
                })}

                {!disabled && application.ChangeTypePermissions.canAddFeesAndPremium &&
                    <SimpleCard
                        key={"defaultFeesAndPremiums"}
                        onClick={() => feesAndPremiumWizardOpen()}
                        onRenderFront={() => {
                            return (
                                <>
                                    <p><Text>Add</Text></p>
                                    <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                                </>
                            );
                        }}
                    />}
            </Stack>

            <FeesGrid
                disabled={disabled}
                creditHidden={true}
            />

            <MonthlyRecurringFeesAgg />

        </CustomPanel>;
    }

    function onRenderStopLoss() {
        return <CustomPanel title={"Stop Loss"}
            canCollapse={true}
            defaultCollapsed={true}
            contentStyle={{ overflow: 'visible' }}
            storageId={"groupConfiguration.stopLoss"}
            mountWhenExpanded={true}
        >
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '15px' }}
            >
                {planStopLoss.map(eachStopLoss => {

                    // skip deactivated records/only show deactivated records if permitted at group config properties level
                    if (eachStopLoss.deactivated !== props.state.data.json.showInActives?.value)
                        return <></>;

                    return (
                        <StopLossCard
                            key={eachStopLoss.id}
                            stopLoss={eachStopLoss}
                            onClick={() => history.push(AppLinkTo(props.state, { id: eachStopLoss.id, type: 'stopLoss' }))}
                        />
                    );
                })}
                {!disabled && application.ChangeTypePermissions.canAddStopLoss &&
                    <SimpleCard
                        key={"defaultStopLoss"}
                        title="Add"
                        onRenderFront={() => {
                            return (
                                <>
                                    <p><Text>Add</Text></p>
                                    <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                                </>
                            );
                        }}
                        onClick={(e) => {
                            stopLossWizardOpen();
                        }}
                    />
                }
            </Stack>
        </CustomPanel>;
    }

    function onRenderAccountsReceivable() {
        return <CustomPanel title={"Accounts Receivable"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"groupConfiguration.accountsReceivable"}
            mountWhenExpanded={true}
        >
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '15px' }}
            >

                {claimsAR.map(eachAccountsReceivable => {

                    // skip deactivated records/only show deactivated records if permitted at group config properties level
                    if (eachAccountsReceivable.deactivated !== props.state.data.json.showInActives?.value)
                        return <></>;

                    return <AccountsReceivableCard
                        key={eachAccountsReceivable.id}
                        accountsReceivable={eachAccountsReceivable}
                        description={eachAccountsReceivable.name}
                        onClick={() => history.push(AppLinkTo(props.state, { id: eachAccountsReceivable.id, type: 'claimsAR' }))}
                    />;

                })}
                {!disabled && application.ChangeTypePermissions.canAddAccountReceivable &&
                    <SimpleCard
                        key={"defaultAccountsReceivable"}
                        title="Add"
                        onRenderFront={() => {
                            return (
                                <>
                                    <p><Text>Add</Text></p>
                                    <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                                </>
                            );
                        }}
                        onClick={(e) => {
                            setARWizardIsOpen(true);
                            setARWizardMount(true);
                        }}
                    />
                }
            </Stack>
        </CustomPanel>;
    }

    function onRenderActionItemsPanel() {
        return <ActionItemsPanel />;
    }

    function onRenderDiagnosisPanel() {
        if (!props.state.status.debug.diagnosis)
            return <></>;

        return <CustomPanel title={"Diagnosis"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"groupConfiguration.diagnosis"}
            mountWhenExpanded={true}
        >
            <Stack style={{ marginTop: '15px' }}>
                <ProcessPanel />
                <DataValidationPanel />
                <SnapshotDiscovery />
            </Stack>
        </CustomPanel>;
    }

    return <BlueRelationshipContext contextMenu={menu}>
        {onRenderPanels()}
        {onRenderActionItemsPanel()}
        {onRenderDiagnosisPanel()}
        {onRenderSummary()}
        {onRenderAttributes()}
        {onRenderPlan()}
        {onRenderBenefit()}
        {onRenderAccountsReceivable()}
        {onRenderGroupStructure()}
        {onRenderFeesAndPremium()}
        {onRenderStopLoss()}

    </BlueRelationshipContext>;
}



function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(GroupConfiguration)
